// src/components/events/EventDetailsSheet.js
// Мобильный лист деталей событий (bottom sheet, тёмный)
import React, { useEffect, useState } from "react";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyIcon from "@mui/icons-material/Key";
import BottomSheet from "../motion/BottomSheet";
import { CARD_BG_BOTTOM, CARD_BG_TOP, CARD_LABEL_COLOR, MUTED_TEXT_COLOR, hexToRgb } from "../../styles/tokens";
import {
  EVENT_TYPE_NAMES,
  GROUP_COLORS,
  GROUP_ICONS,
  GROUP_TITLES,
  cleanValue,
  formatEventTime,
  formatMoney,
  getAttr,
  getWriteoffLabel,
  pluralize,
  sortByDateDesc,
} from "./eventMeta";
import EventTypeChips from "./EventTypeChips";

const SUMMARY_COLOR = "#a1a1aa";
const DISHES_PREVIEW = 3;

const splitDishes = (raw) =>
  (cleanValue(raw) || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && line !== "-");

// Карточка одного события — и в листе событий, и в деталях уведомления
export function EventCard({ event, accent, singleDay }) {
  const [showAllDishes, setShowAllDishes] = useState(false);

  const orderNum = cleanValue(getAttr(event, "orderNum"));
  const title = orderNum ? `Заказ № ${orderNum}` : EVENT_TYPE_NAMES[event.type] || event.type;
  const mainSum = formatMoney(getAttr(event, "sum"));

  // Пары «подпись — значение»; пустые не выводим
  const fields = [
    ["Официант", cleanValue(getAttr(event, "waiter"))],
    ["Выполнил", cleanValue(getAttr(event, "user"))],
    ["Сумма заказа", formatMoney(getAttr(event, "orderSumAfterDiscount"))],
    ["Сотрудник", cleanValue(getAttr(event, "employee"))],
    ["Со списанием", getWriteoffLabel(event)],
    ["Штраф", formatMoney(getAttr(event, "penalty"))],
  ].filter(([, value]) => value !== null);

  const auth = cleanValue(getAttr(event, "auth"));
  const dishes = splitDishes(getAttr(event, "dishes"));
  const visibleDishes = showAllDishes ? dishes : dishes.slice(0, DISHES_PREVIEW);
  const hiddenDishes = dishes.length - DISHES_PREVIEW;
  const reason = cleanValue(getAttr(event, "reason"));
  const comment = cleanValue(getAttr(event, "comment"));

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "14px",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5 }}>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#fff", minWidth: 0, wordBreak: "break-word" }}>
            {title}
          </Typography>
          <Typography
            sx={{ fontSize: "14px", color: SUMMARY_COLOR, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
          >
            {formatEventTime(event.date, singleDay)}
          </Typography>
        </Box>
        {mainSum && (
          <Typography sx={{ mt: 0.5, fontSize: "22px", fontWeight: 800, color: accent, fontVariantNumeric: "tabular-nums" }}>
            {mainSum}
          </Typography>
        )}
      </Box>

      {fields.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1.5,
            "@media (max-width: 339.95px)": { gridTemplateColumns: "1fr" },
          }}
        >
          {fields.map(([label, value]) => (
            <Box key={label} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "12px", color: MUTED_TEXT_COLOR }}>{label}</Typography>
              <Typography sx={{ fontSize: "14px", color: "#f5f5f5", wordBreak: "break-word" }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {auth && (
        <Box
          sx={{
            alignSelf: "flex-start",
            maxWidth: "100%",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: "8px",
            backgroundColor: "rgba(255,255,255,0.08)",
            color: "#f5f5f5",
            fontSize: "13px",
          }}
        >
          <KeyIcon sx={{ fontSize: 16, color: CARD_LABEL_COLOR, flexShrink: 0 }} />
          <Box component="span" sx={{ wordBreak: "break-word" }}>
            Доп. авторизация: {auth}
          </Box>
        </Box>
      )}

      {dishes.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: "12px", color: MUTED_TEXT_COLOR, mb: 0.5 }}>Позиции заказа</Typography>
          {visibleDishes.map((dish, index) => (
            <Typography key={index} sx={{ fontSize: "14px", color: "#f5f5f5", wordBreak: "break-word", lineHeight: 1.5 }}>
              {dish}
            </Typography>
          ))}
          {hiddenDishes > 0 && (
            <ButtonBase
              onClick={() => setShowAllDishes((v) => !v)}
              aria-expanded={showAllDishes}
              sx={{
                mt: 0.5,
                minHeight: 32,
                fontSize: "14px",
                fontWeight: 600,
                color: CARD_LABEL_COLOR,
                borderRadius: "6px",
                "&.Mui-focusVisible": { outline: `2px solid ${accent}`, outlineOffset: "2px" },
              }}
            >
              {showAllDishes ? "Свернуть" : `Ещё ${hiddenDishes}`}
            </ButtonBase>
          )}
        </Box>
      )}

      {reason && (
        <Box>
          <Typography sx={{ fontSize: "12px", color: MUTED_TEXT_COLOR, mb: 0.5 }}>Причина удаления</Typography>
          <Typography sx={{ fontSize: "14px", color: "#f5f5f5", wordBreak: "break-word" }}>{reason}</Typography>
        </Box>
      )}

      {comment && (
        <Box
          sx={{
            backgroundColor: "rgba(255,255,255,0.04)",
            borderRadius: "10px",
            px: 1.5,
            py: 1.25,
            fontSize: "14px",
            color: "#d4d4d8",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          {comment}
        </Box>
      )}
    </Box>
  );
}

function EventDetailsSheet({ open, onClose, onExited, group, events = [], singleDay = false }) {
  const accent = GROUP_COLORS[group] || "#64b5f6";
  const accentRgb = hexToRgb(accent);
  const GroupIcon = GROUP_ICONS[group];
  const title = GROUP_TITLES[group] || group;

  const eventTypes = [...new Set(events.map((e) => e.type))];
  const [activeTab, setActiveTab] = useState("");

  // При смене группы/данных — первая вкладка
  useEffect(() => {
    setActiveTab(eventTypes[0] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, events]);

  // Сводка: «7 событий · 2 340 ₽» (сумма — если есть в данных)
  const sums = events.map((e) => Number(cleanValue(getAttr(e, "sum")))).filter((n) => Number.isFinite(n));
  const totalSum = sums.length > 0 ? sums.reduce((a, b) => a + b, 0) : null;
  const summary = [
    `${events.length} ${pluralize(events.length, "событие", "события", "событий")}`,
    totalSum ? formatMoney(String(totalSum)) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const tabEvents = sortByDateDesc(events.filter((e) => e.type === activeTab));

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      onExited={onExited}
      labelledBy="event-sheet-title"
      paperSx={{
        background: `radial-gradient(420px 200px at 50% 0%, rgba(${accentRgb},0.14), transparent 70%), linear-gradient(180deg, ${CARD_BG_TOP}, ${CARD_BG_BOTTOM})`,
      }}
    >
      {/* Шапка — на месте при скролле списка */}
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, pt: 1.5, pb: 1 }}>
          {GroupIcon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "11px",
                backgroundColor: `rgba(${accentRgb},0.15)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GroupIcon sx={{ fontSize: 22, color: accent }} />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography id="event-sheet-title" sx={{ fontSize: "18px", fontWeight: 800, color: "#fff", lineHeight: 1.25 }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: "14px", color: SUMMARY_COLOR, fontVariantNumeric: "tabular-nums" }}>
              {summary}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Закрыть"
            sx={{ width: 44, height: 44, flexShrink: 0, color: CARD_LABEL_COLOR }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Вкладки-чипсы по подтипам */}
      <EventTypeChips
        eventTypes={eventTypes}
        events={events}
        activeTab={activeTab}
        onChange={setActiveTab}
        accent={accent}
        sx={{ px: 2, py: 1 }}
      />

      {/* Список событий — скроллится только он */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          px: 2,
          pt: 1,
          pb: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        {tabEvents.length === 0 ? (
          <Typography sx={{ py: 5, textAlign: "center", color: MUTED_TEXT_COLOR }}>
            За выбранный период событий нет
          </Typography>
        ) : (
          tabEvents.map((event, index) => (
            <EventCard key={event.id || index} event={event} accent={accent} singleDay={singleDay} />
          ))
        )}
      </Box>
    </BottomSheet>
  );
}

export default EventDetailsSheet;
